import type { GroupSeatData, GroupSeatStatus } from '../groupSubscriptionTypes';

export type FilterKey = 'all' | 'filled' | 'invited' | 'declined' | 'removed' | 'unsubscribed' | 'terminating';

export const FILTER_TABS: Array<{ key: FilterKey; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'filled', label: 'Active' },
  { key: 'invited', label: 'Invited' },
  { key: 'removed', label: 'Removed' },
  { key: 'declined', label: 'Declined invite' },
  { key: 'unsubscribed', label: 'Unsubscribed' },
  { key: 'terminating', label: 'Ends at renewal' },
];

// `terminating` seats still have access until ends_at, so they appear under BOTH
// "Active" (they still hold a seat) and the dedicated "Ends at renewal" tab (the
// subset scheduled to lose access at the next renewal).
export const FILTER_MATCHERS: Record<FilterKey, (seat: GroupSeatData) => boolean> = {
  all: () => true,
  filled: (seat) => seat.status === 'filled' || seat.status === 'terminating',
  invited: (seat) => seat.status === 'invited',
  declined: (seat) => seat.status === 'declined',
  removed: (seat) => seat.status === 'removed',
  unsubscribed: (seat) => seat.status === 'unsubscribed',
  terminating: (seat) => seat.status === 'terminating',
};

export type StatusBadgeStyle = { className: string; label: string };

export const STATUS_STYLES: Record<GroupSeatStatus, StatusBadgeStyle> = {
  invited: {
    className: 'bg-gray-100 text-gray-700 border-gray-200',
    label: 'Invited',
  },
  filled: {
    className: 'bg-emerald-100 text-emerald-900 border-emerald-200',
    label: 'Active',
  },
  terminating: {
    className: 'bg-amber-100 text-amber-900 border-amber-200',
    label: 'Ends at renewal',
  },
  removed: {
    className: 'bg-red-100 text-red-900 border-red-200',
    label: 'Removed',
  },
  declined: {
    // Same neutral styling as `invited` and `unsubscribed` per Figma — the
    // label text alone disambiguates these terminal/inactive states.
    className: 'bg-gray-100 text-gray-700 border-gray-200',
    label: 'Declined invite',
  },
  unsubscribed: {
    className: 'bg-gray-100 text-gray-700 border-gray-200',
    label: 'Unsubscribed',
  },
};

export type BulkBucket = {
  ids: string[];
  count: number;
};

/**
 * Statuses that have at least one bulk action available. `unsubscribed` is
 * deliberately excluded — Rails' `ResendInvitation` rejects unsubscribed seats
 * (they actively opted out of the publication, not just declined a seat
 * invite), so offering a Reinvite affordance would either fail at submit time
 * or, worse, violate the subscriber's stated preference.
 *
 * Source of truth for `computeBulkBuckets` + `isSeatBulkActionable` — keeping
 * one list prevents the bucket/predicate from drifting.
 */
export const BULK_ACTIONABLE_STATUSES: ReadonlyArray<GroupSeatStatus> = [
  'invited',
  'filled',
  'terminating',
  'removed',
  'declined',
];

export function isSeatBulkActionable(seat: GroupSeatData): boolean {
  return BULK_ACTIONABLE_STATUSES.includes(seat.status);
}

// Splits a selection into action-eligible buckets. Bulk action buttons render
// per non-empty bucket, since each status group has different valid actions.
export function computeBulkBuckets(seats: GroupSeatData[]) {
  const invited: string[] = [];
  const active: string[] = [];
  const removed: string[] = [];
  for (const seat of seats) {
    if (seat.status === 'invited') invited.push(seat.id);
    else if (seat.status === 'filled' || seat.status === 'terminating') active.push(seat.id);
    else if (seat.status === 'removed' || seat.status === 'declined') removed.push(seat.id);
    // `unsubscribed` is intentionally not bucketed — see BULK_ACTIONABLE_STATUSES.
  }
  return {
    invited: { ids: invited, count: invited.length } as BulkBucket,
    active: { ids: active, count: active.length } as BulkBucket,
    removed: { ids: removed, count: removed.length } as BulkBucket,
  };
}
